import React, { FC } from "react";
import { ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppMenuKey } from "../../../../../../../../common/routesUtils";
import { useCurrentTheme } from "../../../../../../../../theme/theme";
import { MenuIcon } from "../../../MenuIcon";

interface Props {
	expand?: "more" | "less";
	itemKey: AppMenuKey;
	menuIsOpen: boolean;
	isSelected: boolean;
	onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

/**
 * SectionItem renders a single item of the application side menu which
 * represents an application section.
 *
 * @param expand     if define, controls the "expand" / "collapse" chevron rendered
 *                   after the section name
 * @param itemKey    the menu key representing the section
 * @param menuIsOpen this flag is used to correctly render the menu item
 * @param isSelected the flag indicating if the item is in "selected" state
 * @param onClick    the handler of the "on click" event that may be triggered by users interaction
 * @returns          an item of the application side menu
 */
const SectionItem: FC<Props> = ({ expand, itemKey, menuIsOpen, isSelected, onClick }) => {
	const { t } = useTranslation("navigation");
	const { theme } = useCurrentTheme();
	const title = t([`breadcrumbs/${itemKey}`, `breadcrumbs/<${itemKey}>`]);

	return (
		<ListItem disablePadding>
			<ListItemButton
				selected={isSelected}
				onClick={(e) => onClick(e)}
				sx={{ minHeight: 48, justifyContent: "center" }}
			>
				{menuIsOpen && (
					<ListItemIcon sx={{ minWidth: "auto" }}>
						<MenuIcon target={itemKey} />
					</ListItemIcon>
				)}

				{!menuIsOpen && (
					<Tooltip arrow title={title} placement={"right"}>
						<ListItemIcon sx={{ minWidth: "auto" }}>
							<MenuIcon target={itemKey} />
						</ListItemIcon>
					</Tooltip>
				)}

				{menuIsOpen && (
					<>
						<ListItemText sx={{ textAlign: "start", pl: 1 }} primary={title} />
						{expand && (
							<ListItemIcon
								sx={{
									justifyContent: "end",
								}}
							>
								<MenuIcon
									sx={{
										justifyContent: "end",
										textAlign: "center",
										transition: "transform .125s",
										transform: expand === "less" ? "rotate(180deg)" : "rotate(0deg)",
										color: theme.palette.primary.main,
									}}
									target={AppMenuKey.ArrowDown}
								/>
							</ListItemIcon>
						)}
					</>
				)}
			</ListItemButton>
		</ListItem>
	);
};

export default SectionItem;
